import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, ColumnLayout, Container, Flashbar, Header, SpaceBetween, Tabs } from '@amzn/awsui-components-react-v3';
import {
  convertToDgsHCResourceArn,
  convertToDgsHCResourceId,
  CopiableText,
  DefaultRouteProps,
  fetchTemplatesForResourceId,
  isDGSAdmin,
  keyForCatalogNameMap,
} from 'src/commons/common';
import { WSDatasetList } from 'src/components/workspaces/dataBrowse/database/datasetList';
import { DatabaseTagList } from 'src/components/workspaces/dataBrowse/database/DatabaseTagList';
import { DatabaseEditModal } from 'src/components/workspaces/dataBrowse/database/databaseEdit';
import { listDatabases, listDatasetsForDatabase } from 'src/api/catalog';
import { signedInWithWorkspace } from 'src/components/dataadvisory/constants';

import { getResourceTags } from 'src/api/permissions';
import { AccessSelectModal } from 'src/components/workspaces/dataBrowse/database/dbAccessSelectModal';
import { createCatalogDetailLink } from 'src/routes';
import { GALAXI_DATASOURCE_ID, TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { DataConsumersTable } from 'src/components/permissions/myBaselining/dataConsumersTable';
import { coloredStatus } from 'src/components/resourcesmanager/components';
import { SubscriptionsTable } from 'src/components/catalog/subscriptions/subscriptionsTable';
import MetadataDetails from '../../common/metadataDetails';
import { AdvisoriesForResourceTable } from 'src/components/dataadvisory/listAdvisoriesPage/advisoriesForResource';
import { TemplatesForResourceDetailsTable } from 'src/components/templates/TemplatesForResourceDetailsTable';
import { enableAdvisories, getRegion } from 'src/api/config';
import { ContactInfo } from 'src/components/workspaces/common/ContactInfo';
import { DataBadges } from 'src/components/catalog/common';
import {
  fetchAllGlobalGovernanceTagsInHierarchy,
  getGlossariesForResource,
  lakeFormationIcon,
  ownerLinkItem,
} from 'src/components/workspaces/common/common';
import RegisterTable from 'src/components/catalog/register';
import { DatabaseInfo } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import { ResourceNotFound } from 'src/commons/ResourceNotFound';
import { PageHeader } from 'src/components/common/PageHeader';
import { GalaxiGroup } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { BUSINESS_GLOSSARY_TYPE } from 'src/components/metadata/constants';
import { RESOURCE_TYPE_DATABASE } from 'src/components/permissions/myBaselining/baseliningUtils';
import { DataCatalogResource } from 'src/components/catalog/datasetdetail/datasetDetailHelpers';
import { dpTypeForDataSource } from 'src/components/utils/arnUtil';

export interface WorkspaceDatabaseDetailProps extends DefaultRouteProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  match: any;
  setActiveDatabaseName: any;
  cartItemIds: [];
  addToCart: any;
  catalogMap: any;
  userInfo: any;
  cartItems: any;
  galaxiGroups?: GalaxiGroup[];
}

export const DatabaseDetail = (props: WorkspaceDatabaseDetailProps) => {
  const [database, setDatabase] = useState(undefined);
  const [editDatabaseModalVisible, setEditDatabaseModalVisible] = useState(false);
  const [tags, setTags] = useState(undefined);
  const [accessSelectVisible, setAccessSelectVisible] = useState(undefined);
  const [catalogName, setCatalogName] = useState(undefined);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState(undefined);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(true);
  const [businessGlossaries, setBusinessGlossaries] = useState(undefined);
  const [governanceTags, setGovernanceTags] = useState(undefined);
  const [datasetIds, setDatasetIds] = useState(undefined);

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
  }, []);

  const fetchTags = async (database) => {
    let resourceId = convertToDgsHCResourceId(
      database?.CatalogId,
      database?.ClusterIdentifier,
      database?.RedshiftWorkgroupName,
      database?.BucketName,
      database?.DatabaseName,
      undefined,
      undefined,
      undefined,
      database?.DataSourceId,
    );
    let tagList = [];
    try {
      let getResourceTagsResponse = await getResourceTags({
        resourceId: resourceId,
        statusCustomerType: 'Active:Publisher',
      });
      tagList.push(...getResourceTagsResponse.tags);
    } catch (err) {
      console.log(err);
    }
    setTags(tagList);
  };

  const getDatabaseDatasets = async (database: DatabaseInfo) => {
    let nextToken = null;
    let datasetIds = [];
    do {
      try {
        let datasets = await listDatasetsForDatabase({
          DataSourceId: database.DataSourceId,
          CatalogId: database.CatalogId,
          ClusterIdentifier: database.ClusterIdentifier,
          RedshiftWorkgroupName: database.RedshiftWorkgroupName,
          DatabaseName: database.DatabaseName,
          Region: database.Region,
          NextToken: nextToken,
        });
        const newIds = datasets.DataSetList.map((item) => item.Id);
        datasetIds = datasetIds.concat(newIds);
        nextToken = datasets.NextToken;
      } catch (err) {
        console.log('Exception when fetch datasets for database', err);
        break;
      }
    } while (nextToken != null);
    setDatasetIds(datasetIds);
  };

  const fetchTemplatesForDatabase = async (database) => {
    setLoadingTemplates(true);
    let resourceId = convertToDgsHCResourceId(
      database?.CatalogId,
      database?.ClusterIdentifier,
      database?.RedshiftWorkgroupName,
      database?.BucketName,
      database?.DatabaseName,
      undefined,
      undefined,
      undefined,
      database?.DataSourceId,
    );
    let fetchedTemplates = await fetchTemplatesForResourceId(resourceId);
    setTemplates(fetchedTemplates);
    setLoadingTemplates(false);
  };

  const notifyEditSuccess = async (msg) => {
    //on success update details
    await handleRefresh();
    setNotifications([
      {
        type: 'success',
        content: msg,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  };
  const notifyEditFailure = async (msg) => {
    setNotifications([
      {
        type: 'error',
        content: msg,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  };

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    props.setActiveDatabaseName(props.match.params.databasename);
    handleRefresh();
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const databases = await listDatabases({
        DatabaseKeyList: [
          {
            DataSourceId: props.match.params.datasourceid,
            CatalogId: props.match.params.catalogid,
            DatabaseName: props.match.params.databasename,
            ClusterIdentifier: props.match.params.clustername,
            RedshiftWorkgroupName: props.match.params.workgroupname,
            BucketName: props.match.params.bucketname,
            Region: props.match.params.region ?? getRegion(),
          },
        ],
      });
      let database = databases.DatabaseInfoList[0];
      setDatabase(database);
      setCatalogName(
        props.catalogMap.get(
          keyForCatalogNameMap({
            dataSource: database?.DataSourceId,
            catalogId: database?.CatalogId,
            clusterId: database?.ClusterIdentifier,
            redshiftWorkgroupName: database?.RedshiftWorkgroupName,
            bucketName: database?.BucketName,
            region: database?.Region,
          }),
        ),
      );
      await fetchTags(database);
      await fetchTemplatesForDatabase(database);
      const allGlossaries = await getGlossariesForResource(getArn(database));
      setBusinessGlossaries(allGlossaries.filter((glossary) => BUSINESS_GLOSSARY_TYPE === glossary.GlossarySubType));
      const databaseGlobalGovernanceTags = await fetchAllGlobalGovernanceTagsInHierarchy(allGlossaries);
      setGovernanceTags(databaseGlobalGovernanceTags);

      if (!signedInWithWorkspace(props)) {
        await getDatabaseDatasets(database);
      }
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
    setLoading(false);
    setAccessSelectVisible(false);
  };

  const getTemplateForDatabase = () => {
    return (
      <TemplatesForResourceDetailsTable
        items={templates}
        loading={loadingTemplates}
        userOwnsResource={userOwnsDatabase()}
      />
    );
  };

  const userOwnsDatabase = () => {
    if (!database?.Owners || !props.userInfo) return false;
    if (isDGSAdmin(props.userInfo.memberGroupIds)) return true;
    // Safely handle potentially undefined arrays with nullish coalescing
    const allOwnerIds = new Set([
      ...(props.userInfo.memberWorkspaceIds ?? []),
      ...(props.userInfo.memberGroupIds ?? []),
    ]);
    return database.Owners.some((owner) => allOwnerIds.has(owner));
  };

  const buildResource = (): DataCatalogResource => {
    if (!database) return null;
    return {
      accountId: database.CatalogId,
      region: database.Region,
      type: RESOURCE_TYPE_DATABASE,
      dataCatalogObjectDetails: {
        dataSourceId: database.DataSourceId,
        databaseName: database.DatabaseName,
      },
      dpType: dpTypeForDataSource(database.DataSourceId),
      tagResourceId: `DS-${database.DataSourceId}|A-${database.CatalogId}|DN-${database.DatabaseName}`,
    };
  };

  const getDatabaseDetailsMetadata = (props: any) => {
    const databaseDetails = [];
    if (database?.DatabaseName) {
      databaseDetails.push(
        <CopiableText name={'Database name'} key={'Database name'} value={database?.DatabaseName} />,
      );
    }
    if (database?.Description) {
      databaseDetails.push(<CopiableText name={'Description'} key={'Description'} value={database?.Description} />);
    }
    if (database?.CatalogId) {
      databaseDetails.push(
        <CopiableText
          name={'Catalog ID'}
          key={'Catalog ID'}
          value={database?.CatalogId}
          url={createCatalogDetailLink(
            database?.DataSourceId,
            database?.CatalogId,
            database?.ClusterIdentifier,
            database?.RedshiftWorkgroupName,
            database?.BucketName,
            database?.Region,
          )}
        />,
      );
    }
    if (database?.BucketName) {
      databaseDetails.push(<CopiableText name={'Bucket name'} key={'Bucket name'} value={database?.BucketName} />);
    }
    if (catalogName) {
      databaseDetails.push(
        <CopiableText
          name={'Catalog name'}
          key={'Catalog name'}
          value={catalogName}
          url={createCatalogDetailLink(
            database?.DataSourceId,
            database?.CatalogId,
            database?.ClusterIdentifier,
            database?.RedshiftWorkgroupName,
            database?.BucketName,
            database?.Region,
          )}
        />,
      );
    }
    if (database?.Region) {
      databaseDetails.push(<CopiableText name={'Region'} key={'Region'} value={database?.Region} />);
    }
    if (database?.Owners) {
      databaseDetails.push(
        <CopiableText
          copiable={false}
          name={'Owner'}
          key={'Owner'}
          value={database?.Owners.map((owner) => ownerLinkItem(owner, props.workspaceNameMap))}
        />,
      );
    }
    if (database?.CreatedBy) {
      databaseDetails.push(<CopiableText name={'Created by'} key={'Created by'} value={database?.CreatedBy} />);
    }
    if (database?.CreatedOn) {
      databaseDetails.push(<CopiableText name={'Created on'} key={'CreatedOn'} value={database?.CreatedOn} />);
    }
    if (database?.UpdatedBy) {
      databaseDetails.push(<CopiableText name={'Updated by'} key={'UpdatedBy'} value={database?.UpdatedBy} />);
    }
    if (database?.UpdatedOn) {
      databaseDetails.push(<CopiableText name={'Updated on'} key={'UpdatedOn'} value={database?.UpdatedOn} />);
    }
    if (database?.AutoTableOnboard !== null) {
      databaseDetails.push(
        <CopiableText
          copiable={false}
          name={'Auto-onboard'}
          key={'Auto-onboard'}
          value={coloredStatus(database?.AutoTableOnboard ? 'ENABLED' : 'DISABLED')}
        />,
      );
    }
    if (database?.AllowSNSEventSubscription !== null) {
      databaseDetails.push(
        <CopiableText
          copiable={false}
          name={'Allow event subscriptions'}
          key={'Allow event subscriptions'}
          value={coloredStatus(database?.AllowSNSEventSubscription ? 'ENABLED' : 'DISABLED')}
        />,
      );
    }
    return databaseDetails;
  };

  const closeEditModal = () => {
    setEditDatabaseModalVisible(false);
  };

  const notify = async (type, msg) => {
    setNotifications([
      {
        type: type,
        content: msg,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
  };

  const databaseDetail = () => {
    let tabs = [
      {
        label: 'Details',
        id: 'Details',
        content: (
          <SpaceBetween direction={'vertical'} size={'m'}>
            <Container
              header={
                <Header
                  variant={'h2'}
                  actions={
                    userOwnsDatabase() && (
                      <Button
                        variant='normal'
                        onClick={() => {
                          setEditDatabaseModalVisible(true);
                        }}
                      >
                        Edit
                      </Button>
                    )
                  }
                >
                  Database detail
                </Header>
              }
            >
              <ColumnLayout columns={3} borders='horizontal'>
                {getDatabaseDetailsMetadata(props)}
              </ColumnLayout>
            </Container>
            {!loading && database && <ContactInfo resource={getArn(database)} />}
          </SpaceBetween>
        ),
      },
      {
        label: 'Datasets',
        id: 'Datasets',
        contentRenderStrategy: 'eager',
        content: database ? <WSDatasetList {...props} database={database} /> : <></>,
      },

      {
        label: 'Templates',
        id: 'Templates',
        content: getTemplateForDatabase(),
      },
      {
        label: 'Metadata',
        id: 'Metadata',
        content: (
          <MetadataDetails
            resourceOwnerIds={database?.Owners}
            resource={convertToDgsHCResourceArn(
              database?.CatalogId,
              database?.ClusterIdentifier,
              database?.RedshiftWorkgroupName,
              database?.BucketName,
              database?.DatabaseName,
              undefined,
              undefined,
              undefined,
              database?.DataSourceId,
            )}
            activeGroup={props.activeGroup}
            activeWorkspace={props.activeWorkspace}
            setContentType={props.setContentType}
            setNotification={notify}
          />
        ),
      },
    ];

    if (userOwnsDatabase())
      tabs.push({
        label: 'Consumers',
        id: 'consumers',
        content: <DataConsumersTable {...props} resource={buildResource()} />,
      });

    if (signedInWithWorkspace(props)) {
      tabs.push({
        label: 'Tags',
        id: 'Tags',
        content: (
          <DatabaseTagList
            {...props}
            notifyEditFailure={notifyEditFailure}
            notifyEditSuccess={notifyEditSuccess}
            database={database}
            updateTagLabel={handleRefresh}
          />
        ),
      });
    }

    tabs.push({
      label: 'Subscriptions',
      id: 'Subscriptions',
      content: (
        <SubscriptionsTable
          {...props}
          allowSNSEventSubscription={database?.AllowSNSEventSubscription}
          resource={buildResource()}
        />
      ),
    });

    if (database && database.DataAccessRole !== null && userOwnsDatabase() && !signedInWithWorkspace(props)) {
      tabs.push({
        label: 'Register datasets',
        id: 'Register datasets',
        content: (
          <RegisterTable
            setContentType={(type) => props.setContentType(type)}
            title='Register datasets'
            onNotificationChange={setNotifications}
            extraFeatures={true}
            databaseDetail={database}
            idFilter={datasetIds}
            onlyShowDatasetName={false}
            loading={loading}
            activeGroup={props.activeGroup}
          />
        ),
      });
    }

    if (enableAdvisories()) {
      tabs.push({
        label: 'Advisories',
        id: 'advisories',
        content: (
          <AdvisoriesForResourceTable
            resourceArn={getArn(database)}
            activeGroup={props.activeGroup}
            setContentType={props.setContentType}
          />
        ),
      });
    }

    return (
      <div>
        <Tabs tabs={tabs} />
      </div>
    );
  };

  const getArn = (database) => {
    return convertToDgsHCResourceArn(
      database?.CatalogId,
      database?.ClusterIdentifier,
      database?.RedshiftWorkgroupName,
      database?.BucketName,
      database?.DatabaseName,
      undefined,
      undefined,
      undefined,
      database?.DataSourceId,
    );
  };

  const handleAddToCart = (_) => {
    setAccessSelectVisible(true);
  };

  if (!database && !loading) {
    return (
      <ResourceNotFound
        title={'Database not found'}
        subtitle={
          `The given database is not valid, or you do not have permission to view it. Please\n` +
          ' check the URL for mistakes and try again.'
        }
      />
    );
  }

  return (
    <>
      <Flashbar items={notifications} />
      <SpaceBetween direction={'vertical'} size={'xxs'}>
        <PageHeader
          buttons={
            GALAXI_DATASOURCE_ID != database?.DataSourceId && signedInWithWorkspace(props)
              ? [
                  {
                    text: 'Request Lake Formation access',
                    onItemClick: handleAddToCart,
                    disabled: props.activeWorkspace == null,
                  },
                ]
              : []
          }
          description={database?.Description}
          variant={'h1'}
          header={
            <SpaceBetween size={'s'} direction={'horizontal'}>
              {lakeFormationIcon}
              {database?.DatabaseName}
            </SpaceBetween>
          }
        />
        {
          <DataBadges
            tags={tags}
            businessGlossaries={businessGlossaries}
            database={database}
            governanceTags={governanceTags}
          />
        }
      </SpaceBetween>

      {databaseDetail()}

      {database !== undefined && (
        <DatabaseEditModal
          {...props}
          database={database}
          visible={editDatabaseModalVisible}
          dismiss={closeEditModal}
          notifyEditSuccess={notifyEditSuccess}
          notifyEditFailure={notifyEditFailure}
        />
      )}

      {accessSelectVisible !== undefined && accessSelectVisible && tags && database && (
        <AccessSelectModal
          {...props}
          visible={accessSelectVisible}
          dismiss={() => {
            setAccessSelectVisible(false);
          }}
          // candidateAccess={accessApproaches}
          tags={tags}
          database={database}
          catalogName={catalogName}
        />
      )}
    </>
  );
};
